import React, { useEffect, useRef } from 'react';
import QRCodeStyling, {
  type Options,
  type DotType,
  type CornerSquareType,
  type CornerDotType,
} from 'qr-code-styling';

export interface StyledQRCodeProps {
  /**
   * The URL or data to encode in the QR code
   */
  url: string;
  /**
   * URL of the logo image to display in the center
   */
  logoUrl?: string;
  /**
   * Size of the QR code in pixels (default: 240)
   */
  size?: number;
  /**
   * Main color of the QR code dots and corners (default: #1a3a4a)
   */
  color?: string;
  /**
   * Background color (default: #ffffff)
   */
  bgColor?: string;
  /**
   * Size of the logo relative to QR size (0-1, default: 0.25)
   */
  logoSize?: number;
  /**
   * Style of the dots: 'dots' | 'rounded' | 'classy' | 'classy-rounded' | 'square' | 'extra-rounded'
   */
  dotsType?: DotType;
  /**
   * Style of corner squares: 'dot' | 'square' | 'extra-rounded'
   */
  cornersSquareType?: CornerSquareType;
  /**
   * Style of corner dots: 'dot' | 'square'
   */
  cornersDotType?: CornerDotType;
  /**
   * Additional className for the container
   */
  className?: string;
}

/**
 * StyledQRCode Component
 *
 * Generates a styled QR code with circular dots, circular corner markers,
 * and optional centered logo with white background.
 *
 * @example
 * ```tsx
 * <StyledQRCode
 *   url="https://example.com"
 *   logoUrl="/logo.png"
 *   size={240}
 *   color="#1a3a4a"
 * />
 * ```
 */
export const StyledQRCode: React.FC<StyledQRCodeProps> = ({
  url,
  logoUrl,
  size = 240,
  color = '#1a3a4a',
  bgColor = '#ffffff',
  logoSize = 0.25,
  dotsType = 'dots',
  cornersSquareType = 'dot',
  cornersDotType = 'dot',
  className,
}) => {
  const qrRef = useRef<HTMLDivElement>(null);
  const qrCodeRef = useRef<QRCodeStyling | null>(null);

  useEffect(() => {
    const options: Options = {
      width: size,
      height: size,
      type: 'svg',
      data: url,
      margin: 8,
      qrOptions: {
        typeNumber: 0,
        mode: 'Byte',
        errorCorrectionLevel: 'H', // High error correction for logo overlay
      },
      dotsOptions: {
        type: dotsType,
        color: color,
      },
      cornersSquareOptions: {
        type: cornersSquareType,
        color: color,
      },
      cornersDotOptions: {
        type: cornersDotType,
        color: color,
      },
      backgroundOptions: {
        color: bgColor,
      },
    };

    // Add image options if logoUrl is provided
    if (logoUrl) {
      options.image = logoUrl;
      options.imageOptions = {
        hideBackgroundDots: true,
        imageSize: logoSize,
        margin: 4,
        crossOrigin: 'anonymous',
      };
    }

    // Create new QR code instance
    if (!qrCodeRef.current) {
      qrCodeRef.current = new QRCodeStyling(options);
    } else {
      qrCodeRef.current.update(options);
    }

    // Append to DOM
    if (qrRef.current) {
      qrRef.current.innerHTML = '';
      qrCodeRef.current.append(qrRef.current);
    }
  }, [url, logoUrl, size, color, bgColor, logoSize, dotsType, cornersSquareType, cornersDotType]);

  return (
    <div
      ref={qrRef}
      className={className}
      style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    />
  );
};

export default StyledQRCode;
